export const LOCATION_CACHE_SCHEMA = 1;
export const LOCATION_CACHE_FRESH_MS = 5 * 60 * 1000;
export const LOCATION_CACHE_STALE_MS = 24 * 60 * 60 * 1000;
export const LOCATION_REVALIDATE_MOVEMENT_METERS = 500;
export const LAST_VIEWPORT_STORAGE_KEY = 'pfp:location-cache:last-viewport:v1';

const PUBLIC_LOCATION_FIELDS = Object.freeze([
  'id',
  'title',
  'latitude',
  'longitude',
  'categories',
  'category_slugs',
  'source',
  'is_auto_generated',
  'source_label',
  'updated_at',
  'marker',
  'model_key',
  'loot_label',
  'notice',
]);

function finite(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function normalizePublicLocation(raw = {}) {
  const id = Number(raw?.id ?? raw?.ID ?? raw?.location_id);
  const latitude = finite(raw?.latitude ?? raw?.lat);
  const longitude = finite(raw?.longitude ?? raw?.lng ?? raw?.lon);
  if (!Number.isInteger(id) || id <= 0 || latitude === null || longitude === null) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;

  const result = {};
  for (const key of PUBLIC_LOCATION_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(raw, key)) result[key] = raw[key];
  }
  result.id = id;
  result.latitude = latitude;
  result.longitude = longitude;
  if (Array.isArray(result.categories)) result.categories = result.categories.map(String).slice(0, 12);
  if (Array.isArray(result.category_slugs)) result.category_slugs = result.category_slugs.map(String).slice(0, 12);
  return result;
}

export function sanitizePublicLocations(locations = []) {
  const byId = new Map();
  for (const raw of Array.isArray(locations) ? locations : []) {
    const location = normalizePublicLocation(raw);
    if (location) byId.set(location.id, location);
  }
  return [...byId.values()];
}

export function normalizeCoverageBbox(bbox) {
  const south = finite(bbox?.south);
  const west = finite(bbox?.west);
  const north = finite(bbox?.north);
  const east = finite(bbox?.east);
  if ([south, west, north, east].some((value) => value === null)) return null;
  if (south < -90 || north > 90 || west < -180 || east > 180 || south >= north || west >= east) return null;
  return { south, west, north, east, key: String(bbox?.key || [south, west, north, east].map((value) => value.toFixed(6)).join(',')) };
}

export function bboxContains(outer, inner) {
  const a = normalizeCoverageBbox(outer);
  const b = normalizeCoverageBbox(inner);
  return Boolean(a && b && a.south <= b.south && a.west <= b.west && a.north >= b.north && a.east >= b.east);
}

export function bboxIntersects(left, right) {
  const a = normalizeCoverageBbox(left);
  const b = normalizeCoverageBbox(right);
  return Boolean(a && b && a.north >= b.south && a.south <= b.north && a.east >= b.west && a.west <= b.east);
}

export function locationInsideBbox(location, bbox) {
  const normalized = normalizeCoverageBbox(bbox);
  const latitude = finite(location?.latitude ?? location?.lat);
  const longitude = finite(location?.longitude ?? location?.lng);
  return Boolean(normalized && latitude !== null && longitude !== null
    && latitude >= normalized.south && latitude <= normalized.north
    && longitude >= normalized.west && longitude <= normalized.east);
}

export function bboxCenter(bbox) {
  const normalized = normalizeCoverageBbox(bbox);
  if (!normalized) return null;
  return {
    latitude: (normalized.south + normalized.north) / 2,
    longitude: (normalized.west + normalized.east) / 2,
  };
}

export function distanceMeters(left, right) {
  if (!left || !right) return Number.POSITIVE_INFINITY;
  const lat1 = finite(left.latitude ?? left.lat);
  const lon1 = finite(left.longitude ?? left.lng);
  const lat2 = finite(right.latitude ?? right.lat);
  const lon2 = finite(right.longitude ?? right.lng);
  if ([lat1, lon1, lat2, lon2].some((value) => value === null)) return Number.POSITIVE_INFINITY;
  const radians = (value) => (value * Math.PI) / 180;
  const dLat = radians(lat2 - lat1);
  const dLon = radians(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(radians(lat1)) * Math.cos(radians(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function cacheAgeState(updatedAt, now = Date.now()) {
  const ageMs = Math.max(0, Number(now) - Number(updatedAt || 0));
  return {
    ageMs,
    fresh: Number(updatedAt) > 0 && ageMs <= LOCATION_CACHE_FRESH_MS,
    stale: !Number(updatedAt) || ageMs > LOCATION_CACHE_FRESH_MS,
    veryStale: !Number(updatedAt) || ageMs > LOCATION_CACHE_STALE_MS,
  };
}

export function shouldRevalidateCoverage({ requestedBbox, coverageBbox, updatedAt, lastNetworkCenter, now = Date.now(), force = false } = {}) {
  if (force || !bboxContains(coverageBbox, requestedBbox)) return true;
  const age = cacheAgeState(updatedAt, now);
  if (!age.fresh) return true;
  const center = bboxCenter(requestedBbox);
  return distanceMeters(lastNetworkCenter, center) >= LOCATION_REVALIDATE_MOVEMENT_METERS;
}

export function mergeRevalidatedLocations(current = [], incoming = [], validatedBbox) {
  const next = new Map();
  for (const location of sanitizePublicLocations(current)) {
    if (!locationInsideBbox(location, validatedBbox)) next.set(location.id, location);
  }
  for (const location of sanitizePublicLocations(incoming)) next.set(location.id, location);
  return [...next.values()];
}

export function createCacheRecord({ bbox, locations, updatedAt = Date.now(), etag = '' } = {}) {
  const coverage = normalizeCoverageBbox(bbox);
  if (!coverage) return null;
  return {
    schema: LOCATION_CACHE_SCHEMA,
    key: coverage.key,
    bbox: coverage,
    locations: sanitizePublicLocations(locations),
    updatedAt: Number(updatedAt) || Date.now(),
    etag: String(etag || '').slice(0, 256),
  };
}

export function parseCacheRecord(value) {
  if (!value || Number(value.schema) !== LOCATION_CACHE_SCHEMA) return null;
  return createCacheRecord(value);
}

export function readLastViewport(storage = globalThis?.localStorage) {
  try {
    const value = JSON.parse(storage?.getItem?.(LAST_VIEWPORT_STORAGE_KEY) || 'null');
    const bbox = normalizeCoverageBbox(value?.bbox);
    if (!bbox) return null;
    return {
      bbox,
      center: bboxCenter(bbox),
      zoom: Number.isFinite(Number(value?.zoom)) ? Number(value.zoom) : null,
      savedAt: Number(value?.savedAt || 0),
    };
  } catch {
    return null;
  }
}

export function writeLastViewport(bbox, zoom, storage = globalThis?.localStorage, now = Date.now()) {
  const normalized = normalizeCoverageBbox(bbox);
  if (!normalized) return false;
  try {
    const previous = JSON.parse(storage?.getItem?.(LAST_VIEWPORT_STORAGE_KEY) || 'null');
    const savedZoom = Number.isFinite(Number(zoom)) ? Number(zoom) : (Number.isFinite(Number(previous?.zoom)) ? Number(previous.zoom) : null);
    storage?.setItem?.(LAST_VIEWPORT_STORAGE_KEY, JSON.stringify({ bbox: normalized, zoom: savedZoom, savedAt: now }));
    return true;
  } catch {
    return false;
  }
}

export function createRequestVersionGate() {
  let version = 0;
  return {
    next() { version += 1; return version; },
    isCurrent(candidate) { return candidate === version; },
    current() { return version; },
  };
}
